import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getMoleculeComponents,
  type MoleculeComponentEntry,
  type MoleculeFacts,
  type MoleculeSummary
} from '@peruselab/api-client/molecules';
import { moleculeHead, type CatalogueHead } from './scientificMeta';

const MARK = 'data-catalogue-meta';
const DEFAULT = 'data-catalogue-default';
/** #1119: the server's depiction size (MoleculeDepictions), Open Graph's 1.91:1. */
const DEPICTION_WIDTH = 1200;
const DEPICTION_HEIGHT = 630;

/**
 * ADR 0023 (#1113): what a public molecule page tells search engines and link previews -- its
 * title and description, canonical address, Open Graph and Twitter tags, and Schema.org JSON-LD --
 * in the document's head while the page is shown; the app's defaults come back on leaving it.
 */
export function ScientificMeta({
  summary,
  facts
}: {
  readonly summary: MoleculeSummary;
  readonly facts: MoleculeFacts | null;
}) {
  const { t } = useTranslation();
  const [components, setComponents] = useState<readonly MoleculeComponentEntry[]>([]);
  useEffect(() => {
    let cancelled = false;
    getMoleculeComponents(summary.id)
      .then((view) => {
        if (!cancelled) setComponents(view.components);
      })
      .catch(() => {
        // No ions to name: the structured data takes them from the SMILES.
      });
    return () => {
      cancelled = true;
    };
  }, [summary.id]);

  const head = moleculeHead(t, { summary, facts, components });
  useDocumentHead(head);
  return null;
}

/** #1123: any catalogue page's head -- a drawing's (/i/<id>) as well as a molecule's. */
export function CatalogueMeta({ head }: { readonly head: CatalogueHead }) {
  useDocumentHead(head);
  return null;
}

function useDocumentHead({ title, description, canonical, image, imageAlt, jsonLd }: CatalogueHead) {
  const json = JSON.stringify(jsonLd);
  useEffect(() => {
    const previousTitle = document.title;
    const descriptionTag = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = descriptionTag?.content ?? null;
    document.title = title;
    if (descriptionTag) descriptionTag.content = description;

    const added = [
      ...(descriptionTag ? [] : [element('meta', { name: 'description', content: description })]),
      element('link', { rel: 'canonical', href: canonical }),
      element('meta', { property: 'og:type', content: 'website' }),
      element('meta', { property: 'og:site_name', content: 'PeruseLab' }),
      element('meta', { property: 'og:title', content: title }),
      element('meta', { property: 'og:description', content: description }),
      element('meta', { property: 'og:url', content: canonical }),
      // #1126: a page without a picture (the bond glossary) gets the small card.
      ...(image
        ? [
            element('meta', { property: 'og:image', content: image }),
            element('meta', { property: 'og:image:type', content: 'image/png' }),
            element('meta', { property: 'og:image:width', content: String(DEPICTION_WIDTH) }),
            element('meta', { property: 'og:image:height', content: String(DEPICTION_HEIGHT) }),
            element('meta', { property: 'og:image:alt', content: imageAlt ?? '' })
          ]
        : []),
      element('meta', { name: 'twitter:card', content: image ? 'summary_large_image' : 'summary' }),
      element('meta', { name: 'twitter:title', content: title }),
      element('meta', { name: 'twitter:description', content: description }),
      ...(image
        ? [
            element('meta', { name: 'twitter:image', content: image }),
            element('meta', { name: 'twitter:image:alt', content: imageAlt ?? '' })
          ]
        : []),
      jsonLdScript(json)
    ];
    document.head.append(...added);
    return () => {
      for (const node of added) node.remove();
      document.title = previousTitle;
      if (descriptionTag && previousDescription !== null) descriptionTag.content = previousDescription;
    };
  }, [title, description, canonical, image, imageAlt, json]);
}

/**
 * ADR 0023 D4 (#1117): a catalogue page the server filled in arrives with the molecule's tags in its
 * head (marked) and the app's own title and description kept aside (`data-catalogue-default`). They
 * have done their job -- for readers that don't run the app -- once it starts: it takes the head back
 * then (main.tsx), and ScientificMeta adds its own when the molecule has loaded.
 */
export function takeBackServerHead(): void {
  for (const node of document.head.querySelectorAll(`[${MARK}]`)) node.remove();
  for (const node of document.head.querySelectorAll<HTMLElement>(`[${DEFAULT}]`)) {
    const original = node.getAttribute(DEFAULT) ?? '';
    if (node instanceof HTMLTitleElement) document.title = original;
    else node.setAttribute('content', original);
    node.removeAttribute(DEFAULT);
  }
}

function element(tag: 'meta' | 'link', attributes: Readonly<Record<string, string>>): HTMLElement {
  const node = document.createElement(tag);
  for (const [name, value] of Object.entries(attributes)) node.setAttribute(name, value);
  node.setAttribute(MARK, '');
  return node;
}

function jsonLdScript(json: string): HTMLElement {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  // Inside a <script>, "</" would end it early; JSON allows escaping the slash.
  script.textContent = json.replace(/<\//g, '<\\/');
  script.setAttribute(MARK, '');
  return script;
}
